import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';

/**
 * Replacement for the Cordova `@ionic-native/preview-any-file` plugin.
 *
 * `PreviewAnyFile.preview()` accepted both remote URLs and local file paths and
 * downloaded-then-opened them in a native viewer. `@capacitor-community/file-opener`
 * only opens LOCAL files, so for remote URLs we download to the cache directory
 * first (fetch -> Filesystem) and then open, preserving the old behavior.
 *
 * Keeps a `preview()` method with the same call shape as the old plugin so the
 * call sites change as little as possible.
 */
@Injectable({ providedIn: 'root' })
export class FilePreviewService {
  async preview(pathOrUrl: string, contentType?: string): Promise<void> {
    if (!pathOrUrl) {
      return;
    }

    // No native file opener on the web: open in a new tab (browsers render
    // PDFs/images inline), matching the previous web fallback behavior.
    if (!Capacitor.isNativePlatform()) {
      window.open(pathOrUrl, '_blank');
      return;
    }

    // Already a local file -> open directly.
    if (pathOrUrl.startsWith('file://') || pathOrUrl.startsWith('/')) {
      await FileOpener.open({ filePath: pathOrUrl, contentType });
      return;
    }

    // Remote URL -> download to cache, then open with the native viewer.
    const filePath = await this.downloadToCache(pathOrUrl);
    await FileOpener.open({
      filePath,
      contentType: contentType || this.guessContentType(pathOrUrl),
    });
  }

  private async downloadToCache(url: string): Promise<string> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const base64 = await this.blobToBase64(blob);
    const fileName =
      url.split('/').pop()?.split('?')[0] || `preview-${new Date().getTime()}`;

    // No encoding => Filesystem writes the base64 payload as binary.
    const result = await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });
    return result.uri;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.split(',')[1] || '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private guessContentType(url: string): string | undefined {
    const ext = url.split('?')[0].split('.').pop()?.toLowerCase();
    const map: { [key: string]: string } = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      csv: 'text/csv',
    };
    return ext ? map[ext] : undefined;
  }
}
